import { composeAddress } from './address';

/**
 * Возвращает имя города заявки.
 * Если у заявки выбран справочный city — используем его, иначе custom_city_name.
 */
export function getOrderCity(order) {
    return order?.city_name || order?.custom_city_name || '';
}

/**
 * Возвращает строку "Город, адрес" для заявки.
 * Учитывает, что в `address` город иногда уже включён (исторические данные).
 * - Если адрес начинается с города — возвращаем только адрес (чтобы не было "Ярославль, Ярославль, ...").
 * - Если адреса нет — возвращаем только город.
 * - Если города нет — возвращаем только адрес.
 */
export function formatCityAddress(order) {
    const city = getOrderCity(order).trim();
    const address = (order?.address || '').trim();

    if (!city && !address) return 'Без адреса';

    // composeAddress понимает префиксы населённых пунктов: старая проверка
    // через startsWith пропускала «г Пермь, ул Ленина» при городе «Пермь»
    // и выдавала «Пермь, г Пермь, ул Ленина».
    return composeAddress(city, address) || 'Без адреса';
}

/**
 * Группирует список заявок по городам.
 * Возвращает массив { city: string, orders: Order[] }, отсортированный
 * по количеству заявок в группе (DESC), потом по алфавиту.
 */
export function groupOrdersByCity(orders) {
    const map = new Map();
    for (const o of orders) {
        const city = getOrderCity(o) || 'Без города';
        if (!map.has(city)) map.set(city, []);
        map.get(city).push(o);
    }
    
    return Array.from(map.entries())
        .map(([city, list]) => ({ city, orders: list }))
        .sort((a, b) => {
            if (b.orders.length !== a.orders.length) return b.orders.length - a.orders.length;
            return a.city.localeCompare(b.city, 'ru');
        });
}

/**
 * Считает агрегированную статистику по городам:
 *   [{ city, total, completed, in_progress, planned }]
 * Используется в CityStatsBar.
 */
export function getCityStats(orders) {
    const groups = groupOrdersByCity(orders);
    return groups.map(g => ({
        city: g.city,
        total: g.orders.length,
        completed:   g.orders.filter(o => o.status === 'completed').length,
        in_progress: g.orders.filter(o => o.status === 'in_progress' || o.status === 'on_drawing').length,
        planned:     g.orders.filter(o => o.status === 'measuring_premises').length,
        new:         g.orders.filter(o => o.status === 'new').length,
    }));
}